// ============================================================================
// FILE: frontend/src/modules/ppe/pages/execution/material-issue/MaterialIssueDrawer.js
// MODULE: PPE → Execution → Material Issue
//
// PURPOSE:
//   Drawer UI for issuing reserved materials to shop floor.
//
// FEATURES:
//   - Loads material reservation details
//   - Allows issue qty + scrap entry
//   - Submits material issue
//   - Matches PPE drawer pattern
// ============================================================================

import React, { useEffect, useState } from "react";
import {
  getOperationMaterialDetails,
  issueMaterial
} from "./materialIssueService";

const MaterialIssueDrawer = ({ open, operation, onClose }) => {

  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------------------------------
  // ADDED 2026-02-23 — Validation Helpers
  // --------------------------------------------------------------------------
  const validateRow = (row) => {
    const issueQty = Number(row.issue_quantity || 0);
    const scrapQty = Number(row.scrap_quantity || 0);
    const remaining = Number(row.remaining_qty || 0);

    if (issueQty < 0) return false;
    if (issueQty > remaining) return false;
    if (scrapQty > issueQty) return false;

    return true;
  };

  const isFormValid = () => {
    const rowsWithQty = materials.filter(
      (m) => Number(m.issue_quantity) > 0
    );

    if (rowsWithQty.length === 0) return false;

    return rowsWithQty.every(validateRow);
  };

  // --------------------------------------------------------------------------
// ADDED 2026-02-23 — Live Totals Calculation
// --------------------------------------------------------------------------
    const totals = materials.reduce(
    (acc, row) => {
        acc.issue += Number(row.issue_quantity || 0);
        acc.scrap += Number(row.scrap_quantity || 0);
        return acc;
    },
    { issue: 0, scrap: 0 }
    );

  // --------------------------------------------------------------------------
  // Load material details when drawer opens
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (open && operation) {
      loadMaterialDetails();
    }
  }, [open, operation]);

  const loadMaterialDetails = async () => {
    try {
      setLoading(true);

      const res = await getOperationMaterialDetails(
        operation.plan_operation_id
      );

      if (res.success) {
        const rows = (res.data.materials || []).map((m) => ({
          ...m,
          issue_quantity: "",
          scrap_quantity: ""
        }));

        setMaterials(rows);
      }

    } catch (err) {
      console.error("Failed to load material details", err);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // UPDATED 2026-02-23 — Handle quantity change with guards
  // --------------------------------------------------------------------------
  const handleChange = (index, field, value) => {

    const updated = [...materials];
    updated[index][field] = value;

    // Guard: Issue qty cannot exceed remaining
    if (field === "issue_quantity") {
      const remaining = Number(updated[index].remaining_qty || 0);

      if (Number(value) > remaining) {
        updated[index][field] = remaining;
      }
    }

    // Guard: Scrap cannot exceed issue qty
    if (field === "scrap_quantity") {
      const issueQty = Number(updated[index].issue_quantity || 0);

      if (Number(value) > issueQty) {
        updated[index][field] = issueQty;
      }
    }

    setMaterials(updated);
  };

  // --------------------------------------------------------------------------
  // Submit Issue
  // --------------------------------------------------------------------------
  const handleSubmit = async () => {

    const issueLines = materials
      .filter(m => Number(m.issue_quantity) > 0)
      .map(m => ({
        reservation_line_id: m.reservation_line_id,
        issue_quantity: Number(m.issue_quantity),
        scrap_quantity: Number(m.scrap_quantity || 0)
      }));

    if (issueLines.length === 0) {
      alert("Enter issue quantity");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        remarks,
        issue_lines: issueLines
      };

      const res = await issueMaterial(
        operation.plan_operation_id,
        payload
      );

      if (res.success) {
        alert("Material issued successfully");
        onClose();
      } else {
        alert(res.message || "Issue failed");
      }

    } catch (err) {
      console.error(err);
      alert("Error issuing material");
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // Drawer Hidden
  // --------------------------------------------------------------------------
  if (!open) return null;

  // --------------------------------------------------------------------------
  // UI
  // --------------------------------------------------------------------------
  return (
    <div className="drawer-overlay">
      <div className="drawer-panel">

        {/* ================= HEADER ================= */}
        <div className="drawer-header">
          <h5>Issue Material</h5>
          <button className="btn-close" onClick={onClose}></button>
        </div>

        {/* ================= BODY ================= */}
        <div className="drawer-body">

          <div className="mb-3">
            <strong>Operation:</strong> {operation?.plan_operation_id}
          </div>

          {/* Remarks */}
          <div className="mb-3">
            <label className="form-label">Remarks</label>
            <textarea
              className="form-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {/* Material Table */}
          {loading ? (
            <div>Loading materials...</div>
          ) : (
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Material</th>
                  <th>Reserved</th>
                  <th>Issued</th>
                  <th>Remaining</th>
                  <th width="120">Issue Qty</th>
                  <th width="100">Scrap</th>
                  <th>UOM</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m, index) => (
                  <tr
                    key={m.reservation_line_id}
                    className={!validateRow(m) ? "table-danger" : ""}
                  >
                    <td>
                      {m.material_code} - {m.material_name}
                    </td>
                    <td>{m.reserved_qty}</td>
                    <td>{m.issued_qty}</td>
                    <td>{m.remaining_qty}</td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={m.issue_quantity}
                        onChange={(e) =>
                          handleChange(index, "issue_quantity", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={m.scrap_quantity}
                        onChange={(e) =>
                          handleChange(index, "scrap_quantity", e.target.value)
                        }
                      />
                    </td>

                    <td>{m.uom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* ================= TOTALS BAR (ADDED 2026-02-23) ================= */}
            <div className="mt-3 p-2 border rounded bg-light">
            <div className="row text-end">
                <div className="col-md-6">
                <strong>Total Issue Qty:</strong> {totals.issue}
                </div>
                <div className="col-md-6">
                <strong>Total Scrap Qty:</strong> {totals.scrap}
                </div>
            </div>
            </div>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="drawer-footer">
          <button className="btn btn-secondary me-2" onClick={onClose}>
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving || !isFormValid()}
          >
            {saving ? "Issuing..." : "Issue Material"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default MaterialIssueDrawer;